import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Calendar as CalIcon, Phone } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { useApp } from "../context/AppContext";
import { useTranslation, PHONES } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMES = [
  "12:30","13:00","13:30","14:00","14:30","15:00","15:30","16:00",
  "20:00","20:30","21:00","21:30","22:00","22:30","23:00",
];

export const Reservation = () => {
  const { lang } = useApp();
  const t = useTranslation(lang);

  const [date, setDate] = useState();
  const [time, setTime] = useState("");
  const [guests, setGuests] = useState("2");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!date || !time || !name || !phone) {
      toast.error(lang === "es" ? "Completa los campos obligatorios." : "Please fill the required fields.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/reservations`, {
        name, phone, email: email || null,
        date: format(date, "yyyy-MM-dd"),
        time, guests: parseInt(guests, 10), notes,
      });
      toast.success(t.reservation.success);
      setName(""); setPhone(""); setEmail(""); setNotes(""); setDate(undefined); setTime(""); setGuests("2");
    } catch (err) {
      toast.error(t.reservation.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="reservation" data-testid="reservation-section" className="relative py-16 md:py-24 lg:py-36 bg-[#0E0E0E] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative h-[520px] order-2 lg:order-1 hidden lg:block"
        >
          <img
            src="https://images.unsplash.com/photo-1743592889729-d8a92388412e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
            alt="Q5 Interior"
            className="w-full h-full object-cover border border-q5"
          />
          <div className="absolute -bottom-6 -right-6 bg-[#A91D11] p-6 max-w-[220px]">
            <p className="font-serif-q5 text-3xl text-white leading-none">Q5</p>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/80 mt-2">Moralzarzal · Madrid</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="order-1 lg:order-2"
        >
          <span className="overline">{t.reservation.overline}</span>
          <h2 className="font-serif-q5 text-4xl md:text-5xl mt-3 text-white">{t.reservation.title}</h2>
          <p className="text-neutral-400 mt-4 mb-3">{t.reservation.subtitle}</p>
          <div data-testid="res-call-notice" className="mb-8 border-l-2 border-gold pl-4 py-2 bg-[#A91D11]/10">
            <p className="text-[13px] text-gold/90 leading-relaxed">{t.reservation.callNotice}</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-white text-sm">
              {PHONES.map((p) => (
                <a key={p.tel} href={`tel:${p.tel}`} data-testid={`res-phone-link-${p.tel}`} className="hover:text-gold transition-colors flex items-center gap-1.5">
                  <Phone size={12} className="text-gold" /> {p.display}
                </a>
              ))}
            </div>
          </div>

          <form onSubmit={submit} data-testid="reservation-form" className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <Input
                data-testid="res-name"
                placeholder={t.reservation.name + " *"}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="bg-transparent border-q5 h-12 focus-visible:ring-gold focus-visible:ring-offset-0 rounded-sm"
              />
              <Input
                data-testid="res-phone"
                placeholder={t.reservation.phone + " *"}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                className="bg-transparent border-q5 h-12 focus-visible:ring-gold focus-visible:ring-offset-0 rounded-sm"
              />
            </div>
            <Input
              data-testid="res-email"
              type="email"
              placeholder={t.reservation.email}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-transparent border-q5 h-12 focus-visible:ring-gold focus-visible:ring-offset-0 rounded-sm"
            />

            <div className="grid sm:grid-cols-3 gap-4">
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="res-date-trigger"
                    className="h-12 px-3 border border-q5 bg-transparent rounded-sm text-left text-sm flex items-center gap-2 hover:border-gold transition-colors"
                  >
                    <CalIcon size={16} className="text-gold" />
                    <span className={date ? "text-white" : "text-neutral-500"}>
                      {date ? format(date, "dd MMM yyyy") : t.reservation.pickDate}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  data-testid="res-date-popover"
                  className="w-auto p-0 bg-[#141414] border-q5 z-[60]"
                  align="start"
                >
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                    initialFocus
                    className="bg-[#141414] text-white"
                  />
                </PopoverContent>
              </Popover>

              <Select value={time} onValueChange={setTime}>
                <SelectTrigger data-testid="res-time" className="h-12 bg-transparent border-q5 rounded-sm">
                  <SelectValue placeholder={t.reservation.time + " *"} />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-q5 text-white max-h-72">
                  {TIMES.map((tm) => (
                    <SelectItem key={tm} value={tm} className="focus:bg-[#A91D11]/30">{tm}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={guests} onValueChange={setGuests}>
                <SelectTrigger data-testid="res-guests" className="h-12 bg-transparent border-q5 rounded-sm">
                  <SelectValue placeholder={t.reservation.guests} />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-q5 text-white">
                  {[1,2,3,4,5,6,7,8,10,12].map((g) => (
                    <SelectItem key={g} value={String(g)} className="focus:bg-[#A91D11]/30">{g} {g === 1 ? "✦" : "✦"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Textarea
              data-testid="res-notes"
              placeholder={t.reservation.notes}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="bg-transparent border-q5 focus-visible:ring-gold focus-visible:ring-offset-0 rounded-sm"
            />

            <button
              type="submit"
              data-testid="reservation-submit-btn"
              disabled={loading}
              className="btn-primary-q5 w-full disabled:opacity-60"
            >
              {loading ? "..." : t.reservation.submit}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};
